import { tWithKeyAttr as t } from "../../../i18nHelper";
import i18n from "../../../i18n";
import { usePaycall } from "../../../contexts/PaycallContext";
import { useAppSelector } from "../../../services/hooks";
import { callStateSelector } from "../../../services/selectors/callCenter.selectors";
import styles from "./SipStatusIndicator.module.css";

/**
 * Softphone connection badge.
 *
 *   registered   → green  "טלפון מחובר"
 *   error        → red    "טלפון לא מחובר"  (wrong password / domain / transport down)
 *   anything else→ hidden
 *
 * "unconfigured" (no AgentSipCredentials row) is deliberately silent: most therapists
 * are not call-center agents and should never be told their phone is broken.
 *
 * The red state is the visible half of `isSipReadyForInboundCalls` — while unregistered
 * we suppress incoming-call popups, since the provider never routes the INVITE here and
 * Answer could not work. Without the badge that suppression would be invisible.
 */
function SipStatusIndicator() {
  const { sipStatus, myExtension } = usePaycall();
  const callState = useAppSelector(callStateSelector);

  const isConnected = sipStatus === "registered";
  const isDisconnected = sipStatus === "error";

  if (!isConnected && !isDisconnected) {
    return null;
  }

  // ActiveCallWidget owns the same corner during a call, and the badge is noise then.
  if (callState !== "idle") {
    return null;
  }

  // Native tooltips render \n, so the extension goes on its own line. It is known even
  // in the error case (credentials fetched fine, the REGISTER is what was rejected),
  // which is exactly what an admin needs in order to fix the row.
  const hint = t(
    isConnected
      ? "PAYCALL_PHONE_CONNECTED_HINT"
      : "PAYCALL_PHONE_DISCONNECTED_HINT"
  );
  const tooltip = myExtension
    ? `${hint}\n${t("PAYCALL_EXTENSION")}: ${myExtension}`
    : hint;

  return (
    <div
      className={`${styles.indicator} ${
        isConnected ? styles.indicator_ok : styles.indicator_error
      }`}
      dir={i18n.dir()}
      role="status"
      aria-live="polite"
      // Full explanation stays in the hover tooltip so the badge itself stays small.
      title={tooltip}
      data-testid="sip-status-indicator"
    >
      <span className={styles.icon} aria-hidden>
        ☎
      </span>
      <span className={styles.title}>
        {t(isConnected ? "PAYCALL_PHONE_CONNECTED" : "PAYCALL_PHONE_DISCONNECTED")}
      </span>
    </div>
  );
}

export default SipStatusIndicator;
